import { useMemo } from 'react';
import { ContextZoomType, CanvasMode } from './canvas-types/canvasTypes';
import { sweepNodeCanvasSx } from './nodes/GroupNode';
import { VisibilityLayers } from '../../types/enums/VisibilityLayers';
import { groupLabelStyle } from './nodes/GroupLabelNode';
import {
  contextZoomTypeToClass,
  canvasModeToClass,
  visibilityMapClasses,
} from './nodes/contextZoomConsts';
import merge from 'lodash/merge';
import { useViewport } from '@xyflow/react';
import { getZoomLevels } from './effects/useContextZoomEffect';
import { useSweepCanvasPropsCtx } from './internal-context/SweepCanvasPropsCtx';
import { CONTEXT_ZOOM_LEVEL_1, CONTEXT_ZOOM_LEVEL_1_5, CONTEXT_ZOOM_LEVEL_2 } from './const';
import { useSweepCanvasState } from './internal-context/CanvasStateContext';
import { standaloneNodeStyle } from './nodes/StandaloneNode';
import { getZoomTransformStyle } from './nodes/getZoomTransformStyle';
import { ZOOM_LEVEL_CLASSES } from './nodes/zoomLevelsConsts';
import { getResizeControlStyles } from './nodes/components/selective-aspect-ration-resizer/getResizeControlStyles';

const MAX_LABELS_COUNT = 9999999;

export const useCanvasZoomStyles = () => {
  const { canvasMode = CanvasMode.DEFAULT, sweepGroups, visibilityMap } = useSweepCanvasPropsCtx();
  const { zoomTransformSuffix } = useSweepCanvasState();
  const groupLabelsCount = sweepGroups.length;

  const { zoom } = useViewport();

  const contextZoomType = useMemo(() => {
    let _contextZoom = ContextZoomType.DEFAULT;
    if (!zoom) {
      return _contextZoom;
    }
    if (zoom <= CONTEXT_ZOOM_LEVEL_1) {
      _contextZoom = ContextZoomType.LEVEL1;
    }
    if (zoom <= CONTEXT_ZOOM_LEVEL_1_5 || canvasMode === CanvasMode.PREVIEW2) {
      _contextZoom = ContextZoomType.LEVEL1_5;
    }
    if (zoom <= CONTEXT_ZOOM_LEVEL_2) {
      _contextZoom = ContextZoomType.LEVEL2;
    }
    if (canvasMode === CanvasMode.PREVIEW2) {
      _contextZoom = ContextZoomType.LEVEL1_5;
    }

    return _contextZoom;
  }, [canvasMode, zoom]);

  const contextZoomClass =
    contextZoomTypeToClass[(contextZoomType || ContextZoomType.DEFAULT) as string];
  const canvasModeClass = canvasModeToClass[canvasMode];

  const groupLabelStyleTransformationsStyle = useMemo(() => {
    return groupLabelsCount < MAX_LABELS_COUNT
      ? getZoomTransformStyle(zoom, zoomTransformSuffix)
      : undefined;
  }, [groupLabelsCount, zoom, zoomTransformSuffix]);

  const sx = useMemo(
    () =>
      merge(
        sweepNodeCanvasSx,
        groupLabelStyle,
        standaloneNodeStyle,
        groupLabelStyleTransformationsStyle,
        getResizeControlStyles(zoom),
      ),
    [groupLabelStyleTransformationsStyle, zoom],
  );

  const classes = useMemo(() => {
    const _visibilityMapClasses = Object.entries(visibilityMap).reduce((acc, [key, value]) => {
      if (value) {
        acc.push(visibilityMapClasses[key as VisibilityLayers]);
      }
      return acc;
    }, [] as string[]);

    const {
      isZoomLowerThan0_3,
      isZoomHigherThan0_3,
      isZoomLowerThan0_35,
      isZoomHigherThan0_35,
      isZoomLowerThan0_4,
      isZoomHigherThan0_4,
      isZoomLowerThan0_75,
      isZoomHigherThan0_75,
    } = getZoomLevels(zoom);
    const zoomLevelClasses = [
      isZoomLowerThan0_3 && ZOOM_LEVEL_CLASSES.LOWER_THAN_0_3,
      isZoomHigherThan0_3 && ZOOM_LEVEL_CLASSES.HIGHER_THAN_0_3,
      isZoomLowerThan0_35 && ZOOM_LEVEL_CLASSES.LOWER_THAN_0_35,
      isZoomHigherThan0_35 && ZOOM_LEVEL_CLASSES.HIGHER_THAN_0_35,
      isZoomLowerThan0_4 && ZOOM_LEVEL_CLASSES.LOWER_THAN_0_4,
      isZoomHigherThan0_4 && ZOOM_LEVEL_CLASSES.HIGHER_THAN_0_4,
      isZoomLowerThan0_75 && ZOOM_LEVEL_CLASSES.LOWER_THAN_0_75,
      isZoomHigherThan0_75 && ZOOM_LEVEL_CLASSES.HIGHER_THAN_0_75,
    ].filter(Boolean) as string[];

    return [contextZoomClass, canvasModeClass, ..._visibilityMapClasses, ...zoomLevelClasses];
  }, [canvasModeClass, contextZoomClass, visibilityMap, zoom]);

  return useMemo(() => ({ sx, classes }), [sx, classes]);
};
